import {BaseFields, BaseModel, BaseProps, createFieldDef, FieldDefinition, ModelFieldType} from './baseModel';

import {ModelResponse, ResponseData} from '@dvrd/dvr-controls';

import IDate from '@dvrd/idate';

import User from './userModel';

import {PaymentFailureStatus, PaymentFailureType} from '../util/interfaces';

import {
    deleteFailedPayment,
    adminGenerateNewInvoice,
    getFailedPayments,
    sendFailedPayment,
    generateNewInvoice
} from '../util/requestSender';

interface Props extends BaseProps {
    failure_type?: PaymentFailureType;
    invoice_date?: string;
    amount_excl?: number;
    status?: PaymentFailureStatus;
    payment_url?: string;
    period_from?: string | null;
    period_to?: string | null;
    new_invoice_id?: string;
    has_other?: boolean;
    user_id?: string;
    user?: User | null | any;
}

interface Fields extends BaseFields {
    failure_type: PaymentFailureType;
    invoice_date: string;
    amount_excl: number;
    status: PaymentFailureStatus;
    payment_url: string;
    period_from: string | null;
    period_to: string | null;
    new_invoice_id: string;
    has_other: boolean;
    user_id: string;
    user: User | null | any;
}

const fields: Fields = {
    id: -1n,
    created_at: '',
    updated_at: '',
    failure_type: PaymentFailureType.FAILED,
    invoice_date: '',
    amount_excl: 0,
    status: PaymentFailureStatus.NEW,
    payment_url: '',
    period_from: null,
    period_to: null,
    new_invoice_id: '',
    has_other: false,
    user_id: '',
    user: null
};

const fieldsDefinition: {[key in keyof Omit<Fields, 'user'>]: FieldDefinition} = {
    id: createFieldDef(ModelFieldType.NUMBER),
    created_at: createFieldDef(ModelFieldType.STRING),
    updated_at: createFieldDef(ModelFieldType.STRING, undefined, true),
    invoice_date: createFieldDef(ModelFieldType.STRING),
    amount_excl: createFieldDef(ModelFieldType.NUMBER),
    payment_url: createFieldDef(ModelFieldType.STRING),
    period_to: createFieldDef(ModelFieldType.STRING, null, true),
    period_from: createFieldDef(ModelFieldType.STRING, null, true),
    new_invoice_id: createFieldDef(ModelFieldType.STRING),
    user_id: createFieldDef(ModelFieldType.STRING),
    failure_type: createFieldDef(ModelFieldType.STRING, PaymentFailureType.FAILED),
    status: createFieldDef(ModelFieldType.STRING, PaymentFailureStatus.NEW),
    has_other: createFieldDef(ModelFieldType.BOOLEAN)
};

const ignoreProps: Array<keyof Props> = [];

export default class FailedPayment extends BaseModel<Props, Fields> {
    static getAll = (config: {
        callback: ModelResponse<FailedPayment[]>;
        page: number;
        limit: number;
        search: string | null;
        status: PaymentFailureStatus | null;
    }) => {
        getFailedPayments({
            data: {
                search: config.search,
                status: config.status,
                limit: config.limit,
                page: config.page
            },
            callback: (data: ResponseData) => {
                const payments: Array<FailedPayment> = data.success
                    ? data.payments.map((pay: any) => new FailedPayment(pay))
                    : [];
                config.callback(payments, data.success, data);
            }
        });
    };

    constructor(props?: Props) {
        super(ignoreProps, fieldsDefinition, fields, props);
        this.build(props);
    }

    get id(): bigint {
        return this.fields.id as bigint;
    }

    get failureType(): PaymentFailureType {
        return this.fields.failure_type;
    }

    set failureType(failure_type: PaymentFailureType) {
        this.fields.failure_type = failure_type;
    }

    get invoiceDate(): string {
        return this.fields.invoice_date;
    }

    set invoiceDate(invoice_date: string) {
        this.fields.invoice_date = invoice_date;
    }

    get amountExcl(): number {
        return this.fields.amount_excl;
    }

    set amountExcl(amount_excl: number) {
        this.fields.amount_excl = amount_excl;
    }

    get status(): PaymentFailureStatus {
        return this.fields.status;
    }

    set status(status: PaymentFailureStatus) {
        this.fields.status = status;
    }

    get translatedStatus(): string {
        return {
            [PaymentFailureStatus.NEW]: 'Nieuw',
            [PaymentFailureStatus.GENERATED]: 'Factuur aangemaakt',
            [PaymentFailureStatus.SENT]: 'Factuur verzonden',
            [PaymentFailureStatus.CLOSED]: 'Factuur betaald'
        }[this.status];
    }

    get paymentUrl(): string {
        return this.fields.payment_url;
    }

    set paymentUrl(payment_url: string) {
        this.fields.payment_url = payment_url;
    }

    get newInvoiceId(): string {
        return this.fields.new_invoice_id;
    }

    set newInvoiceId(new_invoice_id: string) {
        this.fields.new_invoice_id = new_invoice_id;
    }

    get userId(): string {
        return this.fields.user_id;
    }

    set userId(user_id: string) {
        this.fields.user_id = user_id;
    }

    get user(): User | null {
        return this.fields.user;
    }

    set user(user: User | null) {
        this.fields.user = user;
        if (user) this.fields.user_id = user.id;
    }

    get periodFromMoment(): IDate | null {
        if (!this.fields.period_from) return null;
        return new IDate(this.fields.period_from);
    }

    get periodToMoment(): IDate | null {
        if (!this.fields.period_to) return null;
        return new IDate(this.fields.period_to);
    }

    get hasOther(): boolean {
        return this.fields.has_other;
    }

    adminGenerateInvoice = (config: {sendImmediately?: boolean; callback: ModelResponse<FailedPayment>}) => {
        adminGenerateNewInvoice({
            data: {
                failed_payment_id: this.id,
                send_immediately: config.sendImmediately,
                user_id: this.userId
            },
            callback: (data: ResponseData) => {
                if (data.success) this.build(data.payment);
                config.callback(this, data.success, data);
            }
        });
    };

    generateInvoice = (config: {sendImmediately?: boolean; callback: ModelResponse<FailedPayment>}) => {
        generateNewInvoice({
            data: {
                failed_payment_id: this.id,
                send_immediately: config.sendImmediately,
                user_id: this.userId
            },
            callback: (data: ResponseData) => {
                if (data.success) this.build(data.payment);
                config.callback(this, data.success, data);
            }
        });
    };

    send = (callback: ModelResponse<FailedPayment>) => {
        sendFailedPayment({
            failed_payment_id: this.id,
            user_id: this.userId,
            callback: (data: ResponseData) => {
                if (data.success) this.build(data.payment);
                callback(this, data.success, data);
            }
        });
    };

    delete = (callback: ModelResponse<FailedPayment>) => {
        deleteFailedPayment({
            failed_payment_id: this.id,
            user_id: this.userId,
            callback: (data: ResponseData) => {
                callback(this, data.success, data);
            }
        });
    };

    protected build = (props?: Props) => {
        this.constructFields(props);
        if (props) {
            if (props.user) this.user = props.user instanceof User ? props.user : new User(props.user);
        }
    };
}
